import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {secondsToMinutes} from "./helpers/secondsToMinutes";
import { getApiErrorMessage } from "./helpers/apiErrorMessage";
import AlbumSearchSkeleton from "./AlbumSearchSkeleton";

function Wishlist() {
    const [query, setQuery] = useState('');
    const [album, setAlbum] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searchCache, setSearchCache] = useState({});
    const [currentResultIndex, setCurrentResultIndex] = useState(0);
    const [searching, setSearching] = useState(false);
    const [searchStage, setSearchStage] = useState('');
    const [searchProgress, setSearchProgress] = useState({});
    const [searchError, setSearchError] = useState(false);
    const [wishlist, setWishlist] = useState([]);
    const [expandedDetails, setExpandedDetails] = useState(null);
    const [diarySettings, setDiarySettings] = useState({ enableRatings: true, showCoverArt: true });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const searchSessionRef = useRef(0);

    useEffect(() => { fetchWishlist(); fetchDiarySettings(); }, []);
    const toggleDetails = (entryId) => { setExpandedDetails(expandedDetails === entryId ? null : entryId); };
    const handleKeyDown = (event) => { if (event.key === 'Enter') { searchAlbum(); } };

    const searchAlbum = async () => {
        if (!query.trim() || searching) return;
        const sessionId = searchSessionRef.current + 1;
        searchSessionRef.current = sessionId;

        setAlbum(null);
        setSearching(true);
        setSearchStage('Finding best matches...');
        setSearchError(false);
        try {
            const response = await axios.get(`/api/search?query=${encodeURIComponent(query)}&mode=basic`);
            const results = Array.isArray(response.data)
                ? response.data
                : response.data?.results || (response.data ? [response.data] : []);

            if (!results.length) {
                setAlbum(null);
                setSearchResults([]);
                setCurrentResultIndex(0);
                setSearchError(true);
                return;
            }

            setSearchResults(results);
            const initialCache = {};
            const initialProgress = {};
            for (const result of results) {
                initialCache[result.mbid] = result;
                initialProgress[result.mbid] = {
                    isLoading: true,
                    label: 'Queued for staged loading...',
                    contextLoaded: false,
                    releaseLoaded: false,
                    artistLoaded: false,
                    coverLoaded: false,
                    hasError: false
                };
            }
            setSearchCache(initialCache);
            setSearchProgress(initialProgress);
            setCurrentResultIndex(0);
            setAlbum(results[0]);

            for (const result of results) {
                preloadAlbumStages(result, initialCache, sessionId);
            }
        } catch (error) {
            console.error("Album not found");
            setSearchError(true);
            setSearchStage('Search failed');
        } finally {
            setSearching(false);
        }
    };

    const mergeAlbumData = (mbid, fallbackAlbum, patch) => {
        setSearchCache(prevCache => {
            const merged = { ...(prevCache[mbid] || fallbackAlbum || {}), ...patch };
            return { ...prevCache, [mbid]: merged };
        });

        setSearchResults(prevResults => prevResults.map(result =>
            result.mbid === mbid ? { ...result, ...patch } : result
        ));

        setAlbum(prevAlbum => prevAlbum?.mbid === mbid ? { ...prevAlbum, ...patch } : prevAlbum);
    };

    const updateProgress = (mbid, patch) => {
        setSearchProgress(prevProgress => ({
            ...prevProgress,
            [mbid]: {
                ...(prevProgress[mbid] || {}),
                ...patch
            }
        }));
    };

    const preloadAlbumStages = async (basicAlbum, initialCache, sessionId) => {
        const mbid = basicAlbum?.mbid;
        if (!mbid) return;

        updateProgress(mbid, { label: 'Loading release-group context...', isLoading: true });

        let releaseId = null;
        let artistId = null;

        try {
            const contextResponse = await axios.get(`/api/search/stage/context/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            releaseId = contextResponse.data?.releaseId;
            artistId = contextResponse.data?.artistId;
            const tags = contextResponse.data?.tags || [];

            mergeAlbumData(mbid, initialCache[mbid], { tags });
            updateProgress(mbid, { contextLoaded: true, label: 'Loading release data...' });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load release-group context'
            });
            return;
        }

        if (releaseId) {
            try {
                const releaseResponse = await axios.get(`/api/search/stage/release/${encodeURIComponent(releaseId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const country = releaseResponse.data?.country || null;
                const duration = releaseResponse.data?.duration || 0;
                mergeAlbumData(mbid, initialCache[mbid], { country, duration });
                updateProgress(mbid, { releaseLoaded: true, label: 'Loading artist data...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load release data'
                });
                return;
            }
        }

        if (artistId) {
            try {
                const artistResponse = await axios.get(`/api/search/stage/artist/${encodeURIComponent(artistId)}`);
                if (searchSessionRef.current !== sessionId) return;

                const artistCountry = artistResponse.data?.country;
                if (artistCountry) {
                    mergeAlbumData(mbid, initialCache[mbid], { country: artistCountry });
                }
                updateProgress(mbid, { artistLoaded: true, label: 'Loading cover art...' });
            } catch (error) {
                if (searchSessionRef.current !== sessionId) return;
                updateProgress(mbid, {
                    hasError: true,
                    isLoading: false,
                    label: 'Could not load artist data'
                });
                return;
            }
        }

        try {
            const coverResponse = await axios.get(`/api/search/stage/cover/${encodeURIComponent(mbid)}`);
            if (searchSessionRef.current !== sessionId) return;

            mergeAlbumData(mbid, initialCache[mbid], {
                albumCover: coverResponse.data?.albumCover || null,
                detailsLoaded: true
            });
            updateProgress(mbid, {
                coverLoaded: true,
                isLoading: false,
                label: 'Album found'
            });
        } catch (error) {
            if (searchSessionRef.current !== sessionId) return;
            updateProgress(mbid, {
                hasError: true,
                isLoading: false,
                label: 'Could not load cover art'
            });
        }
    };

    const fetchWishlist = async () => {
        try {
            setError(null);
            const response = await axios.get('/api/list/wishlist');
            setWishlist(response.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load wishlist entries'));
            console.error('Error fetching wishlist:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDiarySettings = async () => {
        try {
            const response = await axios.get('/api/settings/diary');
            setDiarySettings(response.data);
        } catch (error) {
            setError(getApiErrorMessage(error, 'load diary settings'));
            console.error('Error fetching diary settings:', error);
        }
    };

    const addToWishlist = async () => {
        try {
            setError(null);
            await axios.post('/api/list/wishlist', album);
            await cancelSearch();
            await fetchWishlist();
        } catch (error) {
            setError(getApiErrorMessage(error, 'add this album to wishlist'));
            console.error("Error adding to wishlist");
        }
    };

    const handleDelete = async (entryId) => {
        try {
            setError(null);
            await axios.delete(`/api/list/wishlist/${entryId}`);
            setWishlist(currentWishlist => currentWishlist.filter(entry => entry.listEntryId !== entryId));
            setExpandedDetails(currentExpanded => currentExpanded === entryId ? null : currentExpanded);
        } catch (error) {
            setError(getApiErrorMessage(error, 'delete this wishlist entry'));
            console.error("Error deleting wishlist entry: ", error.message);
        }
    };

    const cancelSearch = async () => {
        searchSessionRef.current += 1;
        setAlbum(null);
        setSearchResults([]);
        setSearchCache({});
        setSearchProgress({});
        setCurrentResultIndex(0);
        setSearchStage('');
    };

    const goToPreviousSearchResult = () => {
        if (currentResultIndex <= 0) return;
        const nextIndex = currentResultIndex - 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const goToNextSearchResult = () => {
        if (currentResultIndex >= searchResults.length - 1) return;
        const nextIndex = currentResultIndex + 1;
        setCurrentResultIndex(nextIndex);
        const nextAlbum = searchResults[nextIndex];
        setAlbum(searchCache[nextAlbum.mbid] || nextAlbum);
    };

    const currentSearchResult = searchResults[currentResultIndex] || null;
    const currentMbid = currentSearchResult?.mbid;
    const currentAlbum = currentMbid ? (searchCache[currentMbid] || currentSearchResult) : album;
    const currentProgress = currentMbid ? searchProgress[currentMbid] : null;
    const showSkeleton = searching || Boolean(currentProgress?.isLoading);

    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    return (
        <div className="bg-gray-900 text-white font-sans min-h-screen">
        <div>
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-400 hover:text-red-200 text-2xl"
                        >
                            x
                        </button>
                    </div>
                </div>
            )}
            <div className="flex flex-col items-center w-full">
                <div className="flex items-center w-full max-w-lg">
                    <input
                        type="text"
                        placeholder="Search for an album or use 'mbid:'"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="flex-grow bg-gray-700 text-gray-300 p-2 mb-5 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button
                        onClick={searchAlbum}
                        disabled={searching}
                        className="bg-green-500 text-white p-2 rounded-r-lg hover:bg-green-600 transition-colors duration-200 mb-5"
                    >
                        {searching ? (
                            <span className="flex items-center gap-2">
                                <i className="fas fa-spinner animate-spin"></i>
                                Searching
                            </span>
                        ) : 'Search'}
                    </button>
                </div>

                {(currentAlbum || showSkeleton) && (
                    <AlbumSearchSkeleton
                        label={showSkeleton ? (searching ? (searchStage || 'Querying MusicBrainz...') : (currentProgress?.label || 'Loading album details...')) : 'Album found'}
                        album={currentAlbum}
                        loading={showSkeleton}
                        showBack={currentResultIndex > 0}
                        showNext={currentResultIndex < searchResults.length - 1}
                        onBack={goToPreviousSearchResult}
                        onNext={goToNextSearchResult}
                    />
                )}

                {searchError && (
                    <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded w-full max-w-lg">
                        <p className="text-red-200">Album not found. Please try a different search.</p>
                        <button 
                            onClick={() => setSearchError(false)}
                            className="text-red-400 hover:text-red-200 text-2xl"
                        >
                            x
                        </button>
                    </div>
                )}

                {currentAlbum && !showSkeleton && (
                    <div key={currentAlbum.mbid}>
                        <div className="flex items-center space-x-4">
                            <button
                                className="bg-green-500 text-white p-2 mb-5 rounded-lg hover:bg-green-600 transition-colors duration-200"
                                onClick={addToWishlist}>Wishlist Album
                            </button>
                            <button
                                className="bg-gray-500 text-white p-2 mb-5 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                                onClick={cancelSearch}>Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6 ml-40 mr-40">
                {wishlist.map((entry, index) => (
                    <div key={entry.listEntryId}
                         className="flex flex-col border-gray-700 hover:bg-gray-800 transition-colors duration-200">
                        <div className="flex items-center border-t border-gray-700 py-4 px-4">
                            <div className="flex items-center">
                                <span className="flex items-center justify-center text-gray-500"
                                        style={{fontSize: '1.25rem'}}>{index + 1}</span>
                            </div>
                            <div className="ml-4 flex-grow flex items-center px-4">
                                {diarySettings.showCoverArt && (
                                    <img
                                        src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                        alt={entry.album.title}
                                        className="inline-block mr-4 w-12 h-12 rounded-lg"
                                    />
                                )}
                                <div className="flex flex-col">
                                    <div className="flex items-center">
                                        <span className="font-bold text-lg">{entry.album.title}</span>
                                        <p className="text-gray-400 text-sm ml-1.5 mt-[3px]">{entry.album.releaseDate}</p>
                                    </div>
                                    <p className="text-gray-400 text-sm">{entry.album.artist}</p>
                                </div>
                            </div>
                            <div className="flex flex-center items-center justify-center mr-6">
                                <i
                                    className={`fas fa-book text-gray-${expandedDetails === entry.listEntryId ? '300' : '500'} mr-2 cursor-pointer hover:text-gray-300`}
                                    onClick={() => toggleDetails(entry.listEntryId)}
                                ></i>
                                <button
                                    onClick={() => handleDelete(entry.listEntryId)}
                                    className="mr-3 p-2 text-gray-500 bg-transparent rounded hover:bg-red-100 hover:text-red-600 cursor-pointer"
                                >
                                    <i className="fas fa-trash-alt"/>
                                </button>
                            </div>
                        </div>
                        <div
                            // id={`details${index}`}
                            // className="overflow-hidden transition-all duration-1000 ease-in-out h-0"
                            className={`overflow-hidden transition-all duration-1000 ease-in-out ${
                                expandedDetails === entry.listEntryId ? 'h-96' : 'h-0'
                            }`}
                        >
                            {expandedDetails === entry.listEntryId && (
                                <div className="flex items-start space-x-20">
                                    <div className="ml-28 mb-4 text-gray-300 space-y-1">
                                        <div className="grid grid-cols-2 w-96 space-y-1">
                                            <p><strong>Artist:</strong> {entry.album.artist}</p>
                                            <p><strong>Title:</strong> {entry.album.title}</p>
                                            <p><strong>Country:</strong> {entry.album.country}</p>
                                            <p><strong>Year:</strong> {entry.album.releaseDate}</p>
                                        </div>
                                        <p><strong>MBID:</strong> {entry.album.mbid}</p>
                                        <p><strong>Duration:</strong> {secondsToMinutes(entry.album.duration)}</p>
                                        <p><strong>Added on:</strong> {entry.addedOn}</p>
                                        <p><strong>Tags:</strong></p><p
                                        className="ml-10 w-[500px]">{entry.album.tags.join(', ')}</p>
                                    </div>
                                    {diarySettings.showCoverArt && (
                                        <img
                                            src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                            alt={entry.album.title}
                                            className="inline-block w-[370px] justify-right"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
        </div>
    );
}

export default Wishlist;